import { useEffect, useRef, useState, useCallback } from 'react';
import { getPendingCommerceMovements, getPendingWarehouseMovements } from 'services/stockMovementsServices';
import { getStockWarehouses } from 'services/stockWarehouseServices';
import UserRoleType from 'types/enums/UserRoleType';

interface AuthLike {
  user?: { role?: UserRoleType; id?: string } | null;
}

interface EntityLike {
  id?: string;
}

export interface EditorDestination {
  role: UserRoleType | undefined;
  commerceId?: string;
  warehouseId?: string;
  loading: boolean;
}

export const useEditorDestination = (authData: AuthLike | null, entity: EntityLike | null): EditorDestination => {
  const role = authData?.user?.role;
  const managerUserId = authData?.user?.id;
  const [warehouseId, setWarehouseId] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (role === UserRoleType.WarehouseEditor && managerUserId) {
      let active = true;
      setLoading(true);
      getStockWarehouses(1, '', managerUserId)
        .then((res) => {
          if (active) setWarehouseId(res?.data?.[0]?.id);
        })
        .catch(() => {
          if (active) setWarehouseId(undefined);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
      return () => {
        active = false;
      };
    }
    return undefined;
  }, [role, managerUserId]);

  if (role === UserRoleType.CommerceEditor) {
    return { role, commerceId: entity?.id, warehouseId: undefined, loading: false };
  }
  if (role === UserRoleType.WarehouseEditor) {
    return { role, commerceId: undefined, warehouseId, loading };
  }
  return { role, loading: false };
};

export interface PendingAcceptance {
  visible: boolean;
  count: number;
}

export const usePendingAcceptance = (authData: AuthLike | null, entity: EntityLike | null): PendingAcceptance => {
  const destination = useEditorDestination(authData, entity);
  const visible = destination.role === UserRoleType.CommerceEditor || destination.role === UserRoleType.WarehouseEditor;
  const [count, setCount] = useState<number>(0);

  const fetchCount = useCallback(async (): Promise<void> => {
    try {
      if (destination.commerceId) {
        const data = await getPendingCommerceMovements(destination.commerceId, 1);
        setCount(data.total ?? 0);
      } else if (destination.warehouseId) {
        const data = await getPendingWarehouseMovements(destination.warehouseId, 1);
        setCount(data.total ?? 0);
      }
    } catch {
      setCount(0);
    }
  }, [destination.commerceId, destination.warehouseId]);

  const fetchCountRef = useRef<() => Promise<void>>(fetchCount);
  useEffect(() => {
    fetchCountRef.current = fetchCount;
  }, [fetchCount]);

  useEffect(() => {
    if (!visible) return undefined;
    fetchCount();
    const interval = setInterval(() => {
      fetchCountRef.current();
    }, 60000);
    const onChanged = (): void => {
      fetchCountRef.current();
    };
    window.addEventListener('pending-movements-changed', onChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('pending-movements-changed', onChanged);
    };
  }, [visible, fetchCount]);

  return { visible, count };
};

export default usePendingAcceptance;
